import React, { useState, useEffect } from 'react';
import { Plus, Edit2, CheckCircle, Trash2 } from 'lucide-react';
import MandateModal from './MandateModal';
import { API_URL } from '../config/api';

export default function MandatesTab({ firmId }) {
    const [mandates, setMandates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingMandate, setEditingMandate] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all', 'open', 'closed'

    useEffect(() => {
        fetchMandates();
    }, [firmId]);

    const fetchMandates = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/firms/${firmId}/mandates`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setMandates(data);
            }
        } catch (error) {
            console.error('Error fetching mandates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMandate = async (mandateData) => {
        try {
            const token = localStorage.getItem('authToken');
            const url = editingMandate
                ? `${API_URL}/mandates/${editingMandate.id}`
                : `${API_URL}/firms/${firmId}/mandates`;
            
            const method = editingMandate ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(mandateData)
            });

            if (!response.ok) {
                throw new Error('Failed to save mandate');
            }

            setModalOpen(false);
            setEditingMandate(null);
            fetchMandates();
        } catch (error) {
            throw error;
        }
    };

    const handleCloseMandate = async (mandateId) => {
        if (!confirm('Mark this mandate as filled?')) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/mandates/${mandateId}/close`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchMandates();
            }
        } catch (error) {
            alert('Failed to close mandate: ' + error.message);
        }
    };

    const handleDeleteMandate = async (mandateId) => {
        if (!confirm('Are you sure you want to delete this mandate?')) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/mandates/${mandateId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchMandates();
            }
        } catch (error) {
            alert('Failed to delete mandate: ' + error.message);
        }
    };

    const openAddModal = () => {
        setEditingMandate(null);
        setModalOpen(true);
    };

    const openEditModal = (mandate) => {
        setEditingMandate(mandate);
        setModalOpen(true);
    };

    const filteredMandates = mandates.filter(m => {
        if (filter === 'open') return m.isOpen;
        if (filter === 'closed') return !m.isOpen;
        return true;
    });

    const openCount = mandates.filter(m => m.isOpen).length;
    const closedCount = mandates.filter(m => !m.isOpen).length;

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <p className="text-sm text-gray-500">Loading mandates...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Open Mandates</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {openCount} open, {closedCount} filled
                        </p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Add Mandate
                    </button>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-4 border-b border-gray-200">
                    <button
                        onClick={() => setFilter('all')}
                        className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                            filter === 'all'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        All ({mandates.length})
                    </button>
                    <button
                        onClick={() => setFilter('open')}
                        className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                            filter === 'open'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Open ({openCount})
                    </button>
                    <button
                        onClick={() => setFilter('closed')}
                        className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                            filter === 'closed'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Filled ({closedCount})
                    </button>
                </div>
            </div>

            {/* Mandates List */}
            {filteredMandates.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                    <p className="text-sm text-gray-500 mb-4">
                        {filter === 'all' ? 'No mandates yet' : `No ${filter} mandates`}
                    </p>
                    {filter === 'all' && (
                        <button
                            onClick={openAddModal}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Add First Mandate
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredMandates.map((mandate) => (
                        <div
                            key={mandate.id}
                            className={`bg-white rounded-lg border p-4 ${
                                mandate.isOpen ? 'border-gray-200' : 'border-gray-100 bg-gray-50'
                            }`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-base font-semibold text-gray-900">
                                            {mandate.positionTitle}
                                        </h3>
                                        {!mandate.isOpen && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-md">
                                                <CheckCircle className="w-3 h-3" />
                                                Filled
                                            </span>
                                        )}
                                    </div>
                                    {mandate.department && (
                                        <p className="text-sm text-gray-600 mb-2">{mandate.department}</p>
                                    )}
                                    {mandate.notes && (
                                        <p className="text-sm text-gray-500 mb-2">{mandate.notes}</p>
                                    )}
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                        <span>Created {new Date(mandate.createdAt).toLocaleDateString()}</span>
                                        {mandate.filledAt && (
                                            <span>Filled {new Date(mandate.filledAt).toLocaleDateString()}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                    {mandate.isOpen && (
                                        <>
                                            <button
                                                onClick={() => openEditModal(mandate)}
                                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                title="Edit"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleCloseMandate(mandate.id)}
                                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                                                title="Mark as Filled"
                                            >
                                                <CheckCircle className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={() => handleDeleteMandate(mandate.id)}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modalOpen && (
                <MandateModal
                    mandate={editingMandate}
                    onSave={handleSaveMandate}
                    onClose={() => {
                        setModalOpen(false);
                        setEditingMandate(null);
                    }}
                />
            )}
        </div>
    );
}